import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable, of, throwError} from 'rxjs';
import {catchError, switchMap, tap} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {SessionStorageService} from '../core/services/session-storage.service';
import {DiagramMetaInterface, User} from './diagram-meta.interface';
import {DiagramMetaService} from './diagram-meta.service';

interface MetaStoreInterface {
  [key: string]: DiagramMetaInterface;
}

@Injectable({providedIn: 'root'})
export class DiagramMetaStoreService {
  private TOKEN_LIFE_TIME =
    1000 * 60 * 60 - // 1 hour
    1000 * 60 * 5; // - 5 minutes
  private getDiagramKey = (diagramId: string, state: string): string => `${diagramId}_${state}`;
  private getUserKey = (userId: string): string => `${userId}_user`;
  private existingNavigationsIds: Set<string> = new Set();
  private storageKeysArray: string[] = [];
  readonly storageKeysMaxSize = 100;
  readonly memoryExceedsRemovedKeysNumber = 25;

  constructor(
    private http: HttpClient,
    private diagramMetaService: DiagramMetaService,
    private sessionStorageService: SessionStorageService
  ) {}

  updateDiagram(diagramId: string, state: string, data: Partial<DiagramMetaInterface>): void {
    const diagramStorageKey = `${diagramId}_${state}`;
    const currentDiagramMeta = SessionStorageService.get(diagramStorageKey);

    if (!currentDiagramMeta) {
      return;
    }

    for (const property in data) {
      if (data.hasOwnProperty(property)) {
        currentDiagramMeta[property] = data[property];
      }
    }

    SessionStorageService.set(diagramStorageKey, currentDiagramMeta);
  }

  fetchDiagram(diagramId: string, state: string) {
    return this.loadFromCache(diagramId, state).pipe(
      switchMap(cached => {
        return cached ? of(cached) : this.loadFromServer(diagramId, state);
      })
    );
  }

  public preparePossibleNavigation(
    diagramId: string,
    state: string
  ): Observable<DiagramMetaInterface[]> {
    const url = `${environment.API_ENDPOINT}/diagram/${diagramId}/state/${state}/possible-navigation`;

    return this.http
      .post<DiagramMetaInterface[]>(url, {existingNavigationsIds: this.getExistingIds()})
      .pipe(
        catchError(err => throwError(() => err)),
        tap(data => this.saveMultipleToCache(state, data))
      );
  }

  private saveMultipleToCache(state, data: DiagramMetaInterface[]) {
    const sixHoursAgo = Date.now() - 6 * 60 * 60 * 1000;

    this.clearClientCache(sixHoursAgo);

    for (const meta of data) {
      this.saveUserToCache(meta);
      this.saveToCache(state, meta);
    }
  }

  private loadFromCache(diagramId: string, state: string): Observable<DiagramMetaInterface | null> {
    const key = this.getDiagramKey(diagramId, state);
    let cached = this.getDiagramMetaSync(key) || null;

    if (cached && cached.user) {
      typeof cached.user === 'string'
        ? (cached.user = SessionStorageService.get(cached.user as string))
        : cached.user;
    }

    if (cached && Date.now() - cached.loadedAt > this.TOKEN_LIFE_TIME) {
      // if kept in cache more than 12 hours
      this.removeDiagramMeta(key);
      cached = null;
    }

    return of(cached);
  }

  private loadFromServer(diagramId, state): Observable<DiagramMetaInterface> {
    const url = `${environment.API_ENDPOINT}/diagram/${diagramId}/state/${state}`;

    return this.http.get<DiagramMetaInterface>(url).pipe(
      catchError(err => throwError(() => err)),
      tap(data => this.saveToCache(state, data))
    );
  }

  private getDiagramMetaSync(key: string): DiagramMetaInterface {
    return SessionStorageService.get(key);
  }

  private removeDiagramMeta(key): void {
    SessionStorageService.remove(key);
  }

  private saveToCache(state: string, diagramMeta): void {
    try {
      const key = this.getDiagramKey(diagramMeta.diagramId, state);

      this.trackSessionStorageKeys(key);
      const exceededMax = this.existingNavigationsIds.size - this.storageKeysMaxSize;

      if (exceededMax > 0) {
        this.sessionStorageService.clearSessionStorage(
          this.storageKeysArray,
          this.existingNavigationsIds,
          exceededMax
        );
      }

      if (diagramMeta.user !== null && typeof diagramMeta.user !== 'string') {
        diagramMeta.user.teams = null;
      }

      const oldMeta = this.getDiagramMetaSync(key) ?? ({} as DiagramMetaInterface);

      const diagramMetaWithViewport = {
        ...diagramMeta,
        ...(oldMeta.viewport && {viewport: oldMeta.viewport}),
        loadedAt: Date.now()
      };

      SessionStorageService.set(key, diagramMetaWithViewport);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        this.sessionStorageService.clearSessionStorage(
          this.storageKeysArray,
          this.existingNavigationsIds,
          this.memoryExceedsRemovedKeysNumber
        );
        this.saveToCache(state, diagramMeta);
      }
    }
  }

  clearClientCache(timestamp: number): void {
    const storage: MetaStoreInterface = this.sessionStorageService.getParsedData();

    Object.keys(storage).forEach(key => {
      if (storage[key] && storage[key].loadedAt < timestamp) {
        SessionStorageService.remove(key);
      }
    });
  }

  updateMasterDiagram(diagramVersionId: string, {userVote}): void {
    const storage: MetaStoreInterface = this.sessionStorageService.getParsedData();

    Object.keys(storage).forEach(key => {
      if (storage[key] && storage[key].id === diagramVersionId) {
        storage[key].userVote = userVote;
        SessionStorageService.set(key, storage[key]);
        this.diagramMetaService.setMeta(storage[key]);
      }
    });
  }

  private getExistingIds(): string[] {
    return this.storageKeysArray.map(key => key.split('_')[0]);
  }

  private trackSessionStorageKeys(key: string): void {
    if (!this.existingNavigationsIds.has(key)) {
      this.storageKeysArray.push(key);
      this.existingNavigationsIds.add(key);
    }
  }

  private saveUserToCache(meta: DiagramMetaInterface): void {
    const user = meta.user as User;
    const userKey = this.getUserKey(user?.userInTeam?.team?.id);

    user.teams = null;
    SessionStorageService.set(userKey, {...user});
    meta.user = userKey;
  }
}
